<template>
  <div class="uploaded-ads-page">
    <div class="page-toolbar">
      <h2 class="page-title">
        Uploaded Ads
      </h2>
      <div class="toolbar-actions">
        <b-form-input
          v-model="search"
          placeholder="Search"
          class="search-input"
          @input="debouncedSearch"
        />
        <b-form-select
          v-model="statusFilter"
          :options="filterOptions"
          class="filter-select"
          @change="reloadPageOne"
        />
      </div>
    </div>

    <div
      v-if="isLoading"
      class="state-panel"
    >
      <b-spinner label="Loading uploaded ads..." />
    </div>

    <div
      v-else-if="loadError"
      class="state-panel state-panel--error"
    >
      <p>{{ loadError }}</p>
      <b-button
        size="sm"
        variant="primary"
        @click="fetchAds"
      >
        Retry
      </b-button>
    </div>

    <div
      v-else-if="!rows.length"
      class="state-panel state-panel--empty"
    >
      <feather-icon
        icon="ImageIcon"
        size="48"
        class="empty-icon"
      />
      <h4>No uploaded ads yet</h4>
      <p>Approved and live ads will appear here.</p>
      <b-button
        variant="primary"
        :to="{ name: 'ads-video-management' }"
      >
        Go to Ads Management
      </b-button>
    </div>

    <div
      v-else
      class="uploaded-grid"
    >
      <div
        v-for="item in rows"
        :key="item.id"
        class="uploaded-card cursor-pointer"
        role="button"
        tabindex="0"
        @click="openAdDetail(item)"
        @keydown.enter.prevent="openAdDetail(item)"
      >
        <img
          :src="item.image"
          alt="Ad preview"
          class="uploaded-image"
        >
        <span
          class="ad-state"
          :class="statusClass(item.statusKey)"
        >
          {{ item.status }}
        </span>
        <div class="uploaded-content">
          <div class="uploaded-head">
            <h5>{{ item.title }}</h5>
            <small>{{ item.createdAt }}</small>
          </div>
          <p class="uploaded-stats">
            {{ item.clicks }} Clicks · {{ item.likes }} Likes · {{ item.reports }} Reports
          </p>
        </div>
      </div>
    </div>

    <AdminPaginationBar
      :page="currentPage"
      :per-page="perPage"
      :total="totalRows"
      @change="onPageChange"
    />
  </div>
</template>

<script>
import {
  BButton,
  BFormInput,
  BFormSelect,
  BSpinner,
} from 'bootstrap-vue'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  applyListPagination,
  debounce,
  formatAdminDate,
  resolveMediaUrl,
  unwrapAdminResponse,
} from '@/utils/adminApiHelpers'

export default {
  name: 'UploadedAds',
  components: {
    BButton,
    BFormInput,
    BFormSelect,
    BSpinner,
    AdminPaginationBar,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      search: '',
      statusFilter: 'all',
      filterOptions: [
        { value: 'all', text: 'All' },
        { value: 'active', text: 'Active' },
        { value: 'approved', text: 'Approved' },
        { value: 'expired', text: 'Expired' },
      ],
      rows: [],
      currentPage: 1,
      perPage: 12,
      totalRows: 0,
    }
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.reloadPageOne()
    }, 400)
    this.fetchAds()
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page
      this.fetchAds()
    },
    reloadPageOne() {
      this.currentPage = 1
      this.fetchAds()
    },
    statusClass(key) {
      if (key === 'expired') return 'is-expired'
      return 'is-active'
    },
    mapAd(row) {
      const status = (row.status || row.ad_status || 'active').toString()
      const statusKey = status.toLowerCase()
      const image = resolveMediaUrl(row.image || row.thumbnail || row.ad_image) || ''
      return {
        id: row.id,
        title: row.title || row.ad_title || 'Untitled ad',
        status: status.charAt(0).toUpperCase() + status.slice(1),
        statusKey,
        image,
        createdAt: formatAdminDate(row.created_at || row.publish_at || ''),
        clicks: row.views ?? row.clicks ?? row.click_count ?? 0,
        likes: row.likes ?? row.like_count ?? 0,
        reports: row.reports ?? row.report_count ?? 0,
      }
    },
    openAdDetail(item) {
      if (!item?.id) return
      this.$router.push({ name: 'ad-detail', params: { id: String(item.id) } })
    },
    async fetchAds() {
      this.isLoading = true
      this.loadError = ''
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
        }
        const q = this.search.trim()
        if (q) params.search = q
        if (this.statusFilter !== 'all') params.status = this.statusFilter

        const res = await adminPanelApi.reportsAds(params)
        const payload = unwrapAdminResponse(res)
        applyListPagination(this, res, payload)
        this.rows = (payload.ads || []).map(r => this.mapAd(r))
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load uploaded ads.'
        this.rows = []
        this.totalRows = 0
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.uploaded-ads-page {
  padding-top: 0.25rem;
}

.page-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  gap: 1rem;
}

.page-title {
  font-weight: 700;
  margin: 0;
}

.toolbar-actions {
  display: flex;
  gap: 0.75rem;
}

.search-input {
  min-width: 200px;
}

.filter-select {
  min-width: 140px;
}

.state-panel {
  text-align: center;
  padding: 3rem 1rem;
  color: #6e6b7b;
}

.state-panel--error {
  color: #ea5455;
}

.empty-icon {
  color: #b9b9c3;
  margin-bottom: 0.75rem;
}

.uploaded-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.uploaded-card.cursor-pointer:hover {
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.1);
}

.uploaded-card {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  position: relative;
}

.uploaded-image {
  width: 100%;
  height: 160px;
  object-fit: cover;
}

.ad-state {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
}

.ad-state.is-active {
  background: rgba(40, 199, 111, 0.9);
  color: #fff;
}

.ad-state.is-expired {
  background: rgba(110, 107, 123, 0.85);
  color: #fff;
}

.uploaded-content {
  padding: 0.85rem 1rem 1rem;
}

.uploaded-head h5 {
  font-size: 1rem;
  margin-bottom: 0.15rem;
}

.uploaded-stats {
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: #6e6b7b;
}
</style>
